import { useState } from 'react';
import { IoMdClose } from 'react-icons/io';
import { FiAlertTriangle } from 'react-icons/fi';
import { toast } from 'react-toastify';
import CheckStatusBadge from './shared/CheckStatusBadge';
import DocumentStatusBadge from './shared/DocumentStatusBadge';
import DraweeStatusBadge from './shared/DraweeStatusBadge';
import PismoStatusBadge from './shared/PismoStatusBadge';
import CityPicker, { CityOption } from './shared/CityPicker';
import { collateralApi } from '../../../services/collateralApi';
import { formatCurrency, formatDate, formatCpfCnpj } from '../../../utils/collateralFormatters';

const PROCESS_LABELS: Record<string, string> = {
    XML: 'XML', INVOICE_PDF: 'NF PDF', RECEIPT_STUB: 'Canhoto',
    ACCEPTED_BILL: 'Duplicata', PURCHASE_CONTRACT: 'Contrato C/V',
};

const Field = ({ label, value }: { label: string; value: React.ReactNode }) => {
    // Render 0 (e.g. attempts) as a real value; only null/undefined/'' fall back to —.
    const display = value === null || value === undefined || value === '' ? '—' : value;
    return (
        <div className="flex justify-between items-center py-1.5">
            <span className="text-xs text-gray-500 dark:text-gray-400">{label}</span>
            <span className="text-xs dark:text-white font-medium text-right max-w-[60%]">{display}</span>
        </div>
    );
};

interface Props {
    item: any;
    onClose: () => void;
    onUpdated?: () => void;
    onCityResolved?: () => void;
}

const ReceivableItemView = ({ item, onClose, onUpdated, onCityResolved }: Props) => {
    const [selectedCity, setSelectedCity] = useState<CityOption | null>(null);
    const [savingCity, setSavingCity] = useState(false);
    const [reprocessing, setReprocessing] = useState(false);

    if (!item) return null;

    const processItems: Record<string, any> = {};
    (item.processItems || []).forEach((pi: any) => { processItems[pi.processType] = pi; });

    const ibgeUnresolved = item.ibgeCodeStatus === 'UNRESOLVED';
    const cr = item.creditRight || null;
    const hasError = ['REJECTED', 'FAILED_RETRYABLE', 'FAILED_PERMANENT'].includes(item.creditRightStatus);
    // Only allow resend once the blocking problem is solved. The correctable blocker
    // handled in this panel is the unresolved city (IBGE); while it is unresolved,
    // resending would just fail again with the same error.
    const canResend = hasError && !ibgeUnresolved;

    const handleSaveCity = async () => {
        if (!selectedCity) return;
        setSavingCity(true);
        try {
            await collateralApi.updateReceivableCity(item.receivableId || item.id, selectedCity.id, item.creditOperationId || item.operationId);
            toast.success('Cidade atualizada. Código IBGE resolvido.');
            setSelectedCity(null);
            // Keep the panel open and reload it so the IBGE status flips to resolved
            // and the resend button in the header becomes enabled — right where the
            // user just acted, instead of forcing them to reopen the record.
            (onCityResolved || onUpdated)?.();
        } catch (e: any) {
            toast.error(e?.data?.message || 'Falha ao atualizar a cidade.');
        } finally {
            setSavingCity(false);
        }
    };

    const handleReprocess = async () => {
        const operationId = item.creditOperationId || item.operationId;
        if (!operationId) {
            toast.error('Operação não identificada para reprocessamento.');
            return;
        }
        setReprocessing(true);
        try {
            const res = await collateralApi.reprocessCreditRights(operationId);
            toast.success(`Reenvio disparado (${res?.reprocessed ?? 0} registro(s)).`);
            onUpdated?.();
        } catch (e: any) {
            toast.error(e?.data?.message || 'Falha ao reenviar registros.');
        } finally {
            setReprocessing(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex justify-end">
            <div className="absolute inset-0 bg-black/30" onClick={onClose} />
            <div className="relative w-[420px] h-full bg-white dark:bg-slate-800 shadow-xl overflow-y-auto">
                {/* Header */}
                <div className="sticky top-0 bg-white dark:bg-slate-800 border-b border-gray-200 dark:border-slate-700 p-4 flex items-center justify-between z-10">
                    <h3 className="text-sm font-semibold dark:text-white">Recebível — {item.titleNumber}</h3>
                    <div className="flex items-center gap-2">
                        {hasError && (
                            <button
                                onClick={handleReprocess}
                                disabled={!canResend || reprocessing}
                                title={ibgeUnresolved
                                    ? 'Resolva a cidade (código IBGE) antes de reenviar'
                                    : 'Reenviar registros com erro desta operação'}
                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                            >
                                {reprocessing ? 'Reenviando...' : 'Reenviar'}
                            </button>
                        )}
                        <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><IoMdClose size={18} /></button>
                    </div>
                </div>

                <div className="p-4 space-y-4">
                    {/* Dados do Recebível */}
                    <div>
                        <h4 className="text-[10px] font-semibold uppercase text-gray-500 dark:text-gray-400 mb-2">Dados do Recebível</h4>
                        <div className="bg-gray-50 dark:bg-slate-900 rounded-lg p-3 space-y-0.5">
                            <Field label="Nº NF" value={item.titleNumber} />
                            <Field label="Chave NFe" value={<span className="font-mono text-[10px] break-all">{item.nfeKey}</span>} />
                            <Field label="Parcela" value={item.receivableNumber} />
                            <Field label="Emissão" value={formatDate(item.issueDate)} />
                            <Field label="Vencimento" value={formatDate(item.dueDate)} />
                            <Field label="Valor" value={formatCurrency(item.amount)} />
                            <Field label="Município/UF" value={[item.city, item.state].filter(Boolean).join(' - ') || '—'} />
                        </div>
                    </div>

                    {/* Código IBGE / correção de cidade */}
                    <div>
                        <h4 className="text-[10px] font-semibold uppercase text-gray-500 dark:text-gray-400 mb-2">Código IBGE</h4>
                        <div className="bg-gray-50 dark:bg-slate-900 rounded-lg p-3 space-y-0.5">
                            <Field
                                label="Situação"
                                value={ibgeUnresolved
                                    ? <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400"><FiAlertTriangle size={12} /> {item.ibgeCodeStatusFormatted || 'Não resolvido'}</span>
                                    : <span className="text-green-600 dark:text-green-400">{item.ibgeCodeStatusFormatted || 'Resolvido'}</span>}
                            />
                            <Field label="Código" value={item.ibgeCode ? <span className="font-mono">{item.ibgeCode}</span> : '—'} />

                            {ibgeUnresolved && (
                                <div className="pt-2 space-y-2">
                                    <div className="text-[11px] text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 rounded p-2">
                                        O código IBGE do município não pôde ser resolvido automaticamente. Selecione a cidade correta para registrar o direito creditório.
                                    </div>
                                    <CityPicker province={item.state} onSelect={setSelectedCity} />
                                    {selectedCity && (
                                        <div className="flex items-center justify-between text-xs">
                                            <span className="dark:text-gray-300">{selectedCity.name} - {selectedCity.uf} <span className="font-mono text-gray-400">({selectedCity.ibgeCode})</span></span>
                                            <button
                                                onClick={handleSaveCity}
                                                disabled={savingCity}
                                                className="px-3 py-1 rounded bg-blue-600 text-white text-xs hover:bg-blue-700 disabled:opacity-50"
                                            >
                                                {savingCity ? 'Salvando...' : 'Salvar'}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Registro na registradora (Pismo) */}
                    <div>
                        <h4 className="text-[10px] font-semibold uppercase text-gray-500 dark:text-gray-400 mb-2">Registro do Direito Creditório</h4>
                        <div className="bg-gray-50 dark:bg-slate-900 rounded-lg p-3 space-y-2">
                            <div className="flex items-center justify-between">
                                <span className="text-xs text-gray-500 dark:text-gray-400">Status</span>
                                <PismoStatusBadge status={item.creditRightStatus} statusFormatted={item.creditRightStatusFormatted} />
                            </div>

                            {cr && (
                                <div className="space-y-0.5 border-t border-gray-200 dark:border-slate-700 pt-2">
                                    <Field label="Registradora" value={cr.registryName} />
                                    <Field label="Nº interno" value={cr.internalNumber ? <span className="font-mono text-[10px] break-all">{cr.internalNumber}</span> : '—'} />
                                    <Field label="Registrado em" value={cr.registeredAt ? formatDate(cr.registeredAt) : '—'} />
                                    <Field label="Tentativas" value={cr.attempts ?? 0} />
                                    {cr.errorCode && <Field label="Erro" value={<span className="text-red-600 dark:text-red-400">{cr.errorCodeFormatted || cr.errorMessage || cr.errorCode}</span>} />}
                                    {cr.errorMessage && (
                                        <div className="text-[11px] text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20 rounded p-2 mt-1">
                                            {cr.errorMessage}
                                        </div>
                                    )}
                                </div>
                            )}

                            {cr && Array.isArray(cr.operationLogs) && cr.operationLogs.length > 0 && (
                                <div className="border-t border-gray-200 dark:border-slate-700 pt-2">
                                    <div className="text-[10px] font-semibold uppercase text-gray-500 dark:text-gray-400 mb-1">Histórico (Pismo)</div>
                                    <ul className="space-y-1 max-h-40 overflow-y-auto">
                                        {cr.operationLogs.map((log: any, i: number) => (
                                            <li key={i} className="text-[11px] flex items-start gap-2">
                                                <span className="text-gray-400 font-mono whitespace-nowrap">{formatDate(log.timestamp)}</span>
                                                <span className="dark:text-gray-300">
                                                    <span className="font-medium">{log.action}</span>
                                                    {log.data && Object.keys(log.data).length > 0 && (
                                                        <span className="text-gray-500 dark:text-gray-400"> — {JSON.stringify(log.data)}</span>
                                                    )}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {(item.creditRightStatus === 'REJECTED' || item.creditRightStatus === 'FAILED_RETRYABLE' || item.creditRightStatus === 'FAILED_PERMANENT') && (
                                <>
                                    {item.creditRightStatus === 'FAILED_PERMANENT' && (
                                        <div className="text-[11px] text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20 rounded p-2">
                                            Falha permanente — verifique os dados (ex.: IBGE, CNPJ) antes de reenviar.
                                        </div>
                                    )}
                                    {ibgeUnresolved && (
                                        <div className="text-[11px] text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 rounded p-2">
                                            Resolva a cidade (código IBGE) acima para habilitar o reenvio no topo.
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>

                    {/* Cedente */}
                    {(item.assignorName || item.assignorTaxId) && (
                        <div>
                            <h4 className="text-[10px] font-semibold uppercase text-gray-500 dark:text-gray-400 mb-2">Cedente</h4>
                            <div className="bg-gray-50 dark:bg-slate-900 rounded-lg p-3 space-y-0.5">
                                <Field label="Nome" value={item.assignorName} />
                                <Field label="CPF/CNPJ" value={formatCpfCnpj(item.assignorTaxId)} />
                            </div>
                        </div>
                    )}

                    {/* Sacado */}
                    <div>
                        <h4 className="text-[10px] font-semibold uppercase text-gray-500 dark:text-gray-400 mb-2">Sacado</h4>
                        <div className="bg-gray-50 dark:bg-slate-900 rounded-lg p-3 space-y-0.5">
                            <Field label="Nome" value={item.debtorName} />
                            <Field label="CPF/CNPJ" value={formatCpfCnpj(item.debtorDocument)} />
                            <Field label="Status" value={item.draweeStatus ? <DraweeStatusBadge status={item.draweeStatus} statusFormatted={item.draweeStatusFormatted || item.draweeStatus} /> : '—'} />
                        </div>
                    </div>

                    {/* Status */}
                    <div>
                        <h4 className="text-[10px] font-semibold uppercase text-gray-500 dark:text-gray-400 mb-2">Status</h4>
                        <div className="bg-gray-50 dark:bg-slate-900 rounded-lg p-3">
                            <div className="flex items-center gap-2 mb-2">
                                <span className="text-xs text-gray-500">Documento:</span>
                                <DocumentStatusBadge status={item.status || ''} statusFormatted={item.statusFormatted || item.status || ''} />
                            </div>
                            {item.observation && (
                                <div className="text-xs text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 rounded p-2 mt-2">
                                    {item.observation}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Validações */}
                    <div>
                        <h4 className="text-[10px] font-semibold uppercase text-gray-500 dark:text-gray-400 mb-2">Validações</h4>
                        <div className="grid grid-cols-2 gap-2">
                            {Object.keys(PROCESS_LABELS).map(p => {
                                const pi = processItems[p];
                                return (
                                    <div key={p} className="p-3 bg-gray-50 dark:bg-slate-900 rounded border border-gray-200 dark:border-slate-700">
                                        <div className="text-[10px] font-semibold text-gray-600 dark:text-gray-300 uppercase mb-1">{PROCESS_LABELS[p]}</div>
                                        {pi ? (
                                            <div className="space-y-0.5">
                                                <Field label="Recebido" value={pi.received ? 'Sim' : 'Não'} />
                                                <Field label="Status" value={<CheckStatusBadge status={pi.status} statusFormatted={pi.statusFormatted || pi.status} />} />
                                                {pi.observation && (
                                                    <div className="text-[11px] text-orange-700 dark:text-orange-300 bg-amber-50 dark:bg-amber-900/20 rounded p-1.5 mt-1">
                                                        {pi.observation}
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <span className="text-xs text-gray-400">Não configurado</span>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ReceivableItemView;
